import { isNewPresenter } from '@/pages/agents/components/avatars/utils/IsNewPresenter';
import { useFlag } from '@/services/featureFlags';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import { Presenter } from '@d-id/types';
import { useCallback, useMemo } from 'react';
import { isUserAvatar, isUserClipAvatar } from './useAvatarsFilters';

export const useAvatarsSorting = () => {
    const { userSettings } = useUserSettings();
    const topAvatars: string[] = useFlag('top_avatars_for_lib', []);
    const favoriteAvatars: string[] = useMemo(() => userSettings?.preferences?.avatars || [], [userSettings?.preferences?.avatars]);

    const sortAvatarsByDate = (avatars: Presenter[]) => {
        return [...avatars].sort((a, b) => {
            return new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime();
        });
    };

    const sortAvatars = useCallback(
        (presenters: Presenter[]) => {
            return [...presenters].sort((a, b) => {
                const isAFavorite = favoriteAvatars.includes(a.id);
                const isBFavorite = favoriteAvatars.includes(b.id);

                if (isAFavorite && !isBFavorite) return -1;
                if (!isAFavorite && isBFavorite) return 1;

                const isAUserAvatar = isUserAvatar(a) || isUserClipAvatar(a);
                const isBUserAvatar = isUserAvatar(b) || isUserClipAvatar(b);

                if (isAUserAvatar && !isBUserAvatar) return -1;
                if (!isAUserAvatar && isBUserAvatar) return 1;

                const isATop = topAvatars.includes(a.id);
                const isBTop = topAvatars.includes(b.id);

                if (isATop && !isBTop) return -1;
                if (!isATop && isBTop) return 1;

                if (isATop && isBTop) {
                    return topAvatars.indexOf(a.id) - topAvatars.indexOf(b.id);
                }

                const isANew = isNewPresenter(a);
                const isBNew = isNewPresenter(b);

                if (isANew && !isBNew) return -1;
                if (!isANew && isBNew) return 1;

                return a.name.localeCompare(b.name);
            });
        },
        [favoriteAvatars, topAvatars]
    );

    return { sortAvatars, sortAvatarsByDate };
};
